import { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';

import excelIcon from '../../images/excel_icon.svg';
import loadIcon from '../../images/loader-transparent.svg';
import noDocumentImage from '../../images/no-document-copy.png';

import styles from './FileUploadManager.module.css'

const TemplateSelectionModal = ({
    api,
    popup,
    navigate,
    isOpen,
    onClose,
    recordId,
    objectType,
}) => {

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateLoadingError, setTemplateLoadingError] = useState('');
  const [templatePage, setTemplatePage] = useState(1);
  const templatePageSize = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  
  const showTemplatesModal = async () => {
    try {
      setTemplateLoadingError('');
      setIsLoadingTemplates(true);
      const response = await api.get('/boldsign/get-templates', {
        params: {
          objectType,
          withEditAccess: false
        }
      });

      if (response.status === 200) {
        const data = await response.data;
        setTemplates(data || []);
      } else {
        const errorText = await response.data;
        setTemplateLoadingError('Failed to load templates. Please try again.');
      }
    }
    catch (error) {
      setTemplateLoadingError('Failed to load templates. Please try again.');
    } finally {
      setIsLoadingTemplates(false);
    }
  }

  const handleTemplateSelect = async (templateId, templateName) => {
    setSelectedTemplate(templateId);
    setSelectedTemplateName(templateName);
  };

  const navigateToCreateTemplate = () => {
    const origin = window.location.origin;
    const url = new URL(`${origin}/createTemplate`);
    if (objectType) url.searchParams.set('objectType', objectType);
    if (popup) url.searchParams.set('popup', '1');
    window.location.href = url.toString();
  };
  
  // Directly use a template (shown as a hover action on each item)
  const handleUseTemplate = (templateId) => {
    if (!templateId) return;
    const origin = window.location.origin;

    const url = new URL(`${origin}/sendTemplate`);
    url.searchParams.set("recordId", recordId);
    url.searchParams.set("objectType", objectType);
    url.searchParams.set("selectedTemplated", templateId);
    if (popup) {
      url.searchParams.set("popup", "1");
    }

    window.location.href = url.toString();
  }
  
  // Filter templates based on search query
  useEffect(() => {
    if (templates) {
      if (!searchQuery) {
        setFilteredTemplates(templates);
      } else {
        const filtered = templates.filter(template => 
          template.templateName.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredTemplates(filtered);
      }
    }
  }, [searchQuery, templates]);

  useEffect(() => {
      if (isOpen) {
        showTemplatesModal();
      }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
        { isOpen === true && (
            <div className={styles.templateModalOverlay}>
                <div className={styles.templateModal}>
                    <div className={styles.templateHeader}>
                        <h1>Choose from template</h1>
                        <div
                          role="button"
                          tabIndex={0}
                          className={styles.closeButton}
                          onClick={() => onClose(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onClose(false);
                            }
                          }}
                        >
                            <span className={`sfIconBase sfCloseIcon`}></span>
                        </div>
                    </div>
                    <div className={styles.searchWithButtonContainer}>
                        <div className={styles.templateSearchContainer}>
                            <span className={`${styles.sfSearchIcon} sfIconBase sfSearchIcon`}></span>
                            <input
                              type="text"
                              className={styles.templateSearchInput}
                              placeholder="Search template"
                              value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                              <button
                                type="button"
                                className={styles.clearSearchButton}
                                aria-label="Clear search"
                                title="Clear"
                                onClick={() => setSearchQuery('')}
                              >
                                <span className={`${styles.iconBase} ${styles.closeIcon} sfIconBase sfCloseIcon`}></span>
                              </button>
                            )}
                        </div>
                    </div>

                    {/* Template List Header */}
                    <div className={styles.templateListHeader}>
                        <h3>Template name</h3>
                    </div>

                    {/* Template List */}
                    <div className={styles.templateListContainer}>
                        <div className={styles.templateList}>
                            {isLoadingTemplates ? (
                                <div className={styles.loadingSpinner}>
                                  <img src={loadIcon} alt="loading" className="spinner-circle"/>
                                </div>
                                ) : templateLoadingError ? (
                                    <div className={styles.noTemplates}>
                                      <div className={styles.emptyState}>
                                        <div className={styles.emptyContent}>
                                          <div className={styles.emptyTitle}>Failed to load templates</div>
                                          <div className={styles.emptyDesc}>
                                            {templateLoadingError}
                                          </div>
                                        </div>
                                        <button className={styles.emptyCreateLink} onClick={showTemplatesModal}>Retry</button>
                                      </div>
                                    </div>
                                ) : filteredTemplates && filteredTemplates.length > 0 ? ((() => {
                                    const total = filteredTemplates.length;
                                    const startIndex = (templatePage - 1) * templatePageSize;
                                    const endIndex = Math.min(startIndex + templatePageSize, total);
                                    const pageItems = filteredTemplates.slice(startIndex, endIndex);
                                    return (
                                        <>
                                            {pageItems.map((template, index) => (
                                                <div
                                                  key={template.templateId || `${startIndex}-${index}`}
                                                  className={`${styles.templateListItem}`}
                                                >
                                                  <span className={styles.templateName} title={template.templateName}>{template.templateName}</span>
                                                  <button
                                                    type="button"
                                                    className={styles.useButton}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleUseTemplate(template.templateId);
                                                    }}
                                                    aria-label="Use this template"
                                                    title="Use"
                                                  >
                                                    Use
                                                  </button>
                                                </div>
                                            ))}
                                            <div className={styles.paginationBar}>
                                                <span className={styles.paginationInfo}>{`${startIndex + 1} - ${endIndex} of ${total}`}</span>
                                                <div className={styles.paginationActions}>
                                                    <button
                                                        className={styles.pageNavBtn}
                                                        onClick={() => setTemplatePage(p => Math.max(1, p - 1))}
                                                        disabled={templatePage === 1}
                                                        aria-label="Previous"
                                                    >
                                                        <span className="sfIconBase sfLeftArrowIcon"></span>
                                                    </button>
                                                    <button
                                                      className={styles.pageNavBtn}
                                                      onClick={() => setTemplatePage(p => (endIndex >= total ? p : p + 1))}
                                                      disabled={endIndex >= total}
                                                      aria-label="Next"
                                                    >
                                                      <span className="sfIconBase sfRightArrowIcon"></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()) : (
                                    <div className={styles.noTemplates}>
                                        <div className={styles.emptyState}>
                                          <img className={styles.emptyImage} src={noDocumentImage} alt="No templates" />
                                          <div className={styles.emptyContent}>
                                          <div className={styles.emptyTitle}>No templates available!</div>
                                          <div className={styles.emptyDesc}>
                                            You don&apos;t have any saved templates yet. Templates help you quickly send commonly used documents—create one to save time.
                                          </div>
                                          </div>
                                          <button className={styles.emptyCreateLink} onClick={navigateToCreateTemplate}><span className={`${styles.sfAddIcon} sfIconBase sfAddIcon`}></span><span>Create template</span></button>
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        )}  
    </>
  );
};

export default TemplateSelectionModal;